import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { message } from 'antd';
import { useNavigate } from 'react-router';
import { GoogleLogin } from '@react-oauth/google';
import { usePostHog } from '@posthog/react';
import { authActions } from '../../actions/auth';
import authSlice from '../../utils/redux/slice/authSlice';
import './AuthScreen.css';

function RegisterScreen() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const data = await authActions.googleAuth(credentialResponse.credential);
      dispatch(authSlice.actions.setUser(data.user));
      dispatch(authSlice.actions.setToken(data.token));
      dispatch(authSlice.actions.setRefreshToken(data.refreshToken));
      dispatch(authSlice.actions.setPermissions(data.permissions));

      // PostHog: Identify user and capture signup event for Google OAuth
      posthog?.identify(data.user?.client_id || data.user?.client_email, {
        email: data.user?.client_email,
        name: data.user?.client_name,
      });
      posthog?.capture('user_signed_up', {
        method: 'google',
      });

      message.success('Account created successfully!');
    } catch (error) {
      console.error('Google signup error:', error);
      posthog?.capture('signup_failed', {
        method: 'google',
        error_status: error?.response?.status,
      });
      const errorMsg = error?.response?.data?.error || 'Google sign-up failed. Please try again.';
      message.error(errorMsg);
    }
  };

  const handleGoogleError = () => {
    message.error('Google sign-up failed. Please try again.');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    const { email, firstName, lastName, password } = form;
    try {
      await authActions.register(email, firstName, lastName, password);

      // PostHog: Capture signup event (user will be identified after email verification)
      posthog?.capture('user_signed_up', {
        method: 'email',
        email: email,
      });

      message.success('Verification email sent');
      navigate('/verify-email', { state: { email } });
    } catch (error) {
      console.error('Registration error:', error?.response?.status || error?.message);

      // PostHog: Capture signup failure
      posthog?.capture('signup_failed', {
        method: 'email',
        error_status: error?.response?.status,
      });

      // Safely extract error message
      let errorMessage = 'Registration failed. Please try again.';

      try {
        // Handle HTTP status codes
        if (error?.response?.status === 429) {
          errorMessage = 'Too many registration attempts. Please wait a moment and try again.';
        } else if (error?.response?.data?.error) {
          const serverError = error.response.data.error;
          if (typeof serverError === 'string') {
            if (serverError === 'User already exists') {
              errorMessage = 'An account with this email already exists. Please use a different email or try logging in.';
            } else {
              errorMessage = serverError;
            }
          }
        } else if (error?.response?.status) {
          // Handle other HTTP status codes
          switch (error.response.status) {
            case 400:
              errorMessage = 'Invalid registration data. Please check your information.';
              break;
            case 500:
              errorMessage = 'Server error. Please try again later.';
              break;
            default:
              errorMessage = `Registration failed (${error.response.status}). Please try again.`;
          }
        } else if (error?.message && typeof error.message === 'string') {
          errorMessage = error.message;
        }
      } catch (parseError) {
        // ignore parse error
      }

      message.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="auth-container">
      {[...Array(6)].map((_, i) => (
        <span key={i} className="bubble" />
      ))}
      <div className="auth-card">
        <div className="logo-container">
          <img src="/uro_logo_white.png" alt="URO" className="logo" />
        </div>
        <h2>Register</h2>
        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="firstName">First Name</label>
            <input
              id="firstName"
              type="text"
              value={form.firstName}
              onChange={(e) => setForm({ ...form, firstName: e.target.value })}
              autoComplete="given-name"
              placeholder="John"
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="lastName">Last Name</label>
            <input
              id="lastName"
              type="text"
              value={form.lastName}
              onChange={(e) => setForm({ ...form, lastName: e.target.value })}
              autoComplete="family-name"
              placeholder="Doe"
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="registerEmail">Email</label>
            <input
              id="registerEmail"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              autoComplete="email"
              placeholder="john@example.com"
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="registerPassword">Password</label>
            <input
              id="registerPassword"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              autoComplete="new-password"
              placeholder="Min. 6 characters"
              required
              minLength={6}
            />
          </div>
          <button
            className="button"
            type="submit"
            disabled={isSubmitting}
            style={{ opacity: isSubmitting ? 0.65 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
          >
            {isSubmitting ? 'Creating account…' : 'Register'}
          </button>

          <div className="divider">
            <span>or</span>
          </div>

          <div className="google-button-wrapper">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              theme="outline"
              size="large"
              shape="pill"
              text="continue_with"
            />
          </div>

          <button
            type="button"
            className="link-button"
            onClick={() => navigate('/login')}
          >
            Already have an account? Login
          </button>
        </form>
      </div>
    </div>
  );
}

export default RegisterScreen;
